import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchMatchPlayerStats,
  buildPlayerRatingLookup,
} from "../../../lib/playerStats";
import type { PlayerRatingEntry } from "../../../types/playerStats";

/**
 * EPIC-07 — Per-Player Match Score (Rating Engine), cockpit fetch-and-merge
 * hook (fe-epic07, T2, criterion 3).
 *
 * The cockpit's OWN player stats (`usePlayerStats.ts`) are a pure
 * client-side replay of logged events and never call the backend — that is
 * correct and unchanged for every other stat in `PlayerStatsTable`/
 * `PlayerAnalyticsView`. The rating is different: it has exactly ONE
 * source, the backend's `GET /dashboard/matches/{id}/player-stats`
 * response (the SAME endpoint `ServerPlayerStatsSection` already
 * consumes via `fetchMatchPlayerStats`). This hook fetches that response
 * and merges it by `player_id` — it must never re-implement the rating
 * formula in TypeScript.
 *
 * Cockpit-safety (this is the riskiest surface — used live, mid-match):
 *   - `enabled: Boolean(matchId)` — never fires with no match.
 *   - `retry: 1` — one retry, then settles into `isError`, never spins
 *     forever on a broken connection.
 *   - A rejected fetch (offline, a 404 because the match lives only in
 *     `db.match_logs` and the authed dashboard route has no fallback for
 *     that — a pre-existing, documented gap, see `team_findings.md`) is
 *     never thrown into the render tree: React Query's `isError` just
 *     leaves `ratingByPlayerId` `undefined`, exactly like a cold, unloaded
 *     state. Every other column in the table is completely unaffected.
 *   - Live-gated poll, same 30s cadence and rationale as the sibling
 *     `useAdvancedMetrics` hook (PMA-COCK-098 follow-up): a rating is not
 *     sub-second-sensitive, but during a live match it should not require
 *     a manual reload to reflect newly logged events either.
 */
export function usePlayerRatings(
  matchId: string | undefined,
  isLive: boolean = false,
) {
  const query = useQuery({
    queryKey: ["playerRatings", matchId],
    queryFn: () => fetchMatchPlayerStats(matchId!),
    enabled: Boolean(matchId),
    staleTime: 30_000,
    retry: 1,
    refetchInterval: isLive ? 30_000 : false,
  });

  const ratingByPlayerId = useMemo<
    Record<string, PlayerRatingEntry> | undefined
  >(() => {
    if (!query.data?.players?.length) return undefined;
    return buildPlayerRatingLookup(query.data);
  }, [query.data]);

  return {
    ratingByPlayerId,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
